import { CurrentOrganizationUser } from '@organization/api/requests'
import { useAppConfig, useApplicationIcon } from '@organization/hooks'
import { applicationTypes } from '@organization/layout/Applications/AppIcons'
import { useStyles } from '@organization/layout/Applications/styles'
import { GridView, ListView } from '@organization/layout/Applications/Views'
import { AppNames } from '@shared/types'
import { Button, Col, Empty, Flex, Input, Popover, Row, Space } from 'antd'
import classNames from 'classnames'
import { ArrowRight2, Grid1, RowVertical } from 'iconsax-react'
import { ChangeEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  organizationData?: CurrentOrganizationUser
  isCollapsed?: boolean
}

const enum ViewTypes {
  LIST = 'list',
  GRID = 'grid'
}

/**
 *
 * @param organizationData - [SessionData](../../types/layout.ts)
 * @param isCollapsed - The state of the sidebar
 * @description This component is used to switch between different applications in the organization.
 * @example
 * ```tsx
 * <AppSelector organizationData={organizationData} isCollapsed={isCollapsed} />
 * ```
 */

export const AppSelector = ({ organizationData, isCollapsed }: Props) => {
  const [search, setSearch] = useState('')
  const [viewType, setViewType] = useState<ViewTypes>(ViewTypes.LIST)

  const { t } = useTranslation()
  const matchedApp = useAppConfig()

  const isDefaultApp = matchedApp?.key === AppNames.Default

  const applicationIcon = useApplicationIcon(
    organizationData?.current_application?.type ?? ''
  )
  const { styles } = useStyles({ applicationIcon })

  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }, [])

  const filteredApplications = organizationData?.pinned_applications.filter(
    (app) =>
      app?.instance_type?.name?.toLowerCase().includes(search.toLowerCase())
  )

  const currentApplicationType = isDefaultApp
    ? AppNames.Default
    : organizationData?.current_application?.type ?? AppNames.Default

  const { icon: AppIcon } =
    applicationTypes[currentApplicationType] ?? applicationTypes.default

  return (
    <Popover
      styles={{
        body: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          padding: 0,
          marginLeft: '13px',
          marginTop: '-16px'
        }
      }}
      content={() => (
        <Flex vertical className={styles.popoverWrapper}>
          <Flex gap={12} className={styles.view}>
            <Input
              placeholder={t('application.search')}
              value={search}
              onChange={onSearch}
              allowClear
            />
            <Space>
              <Button
                icon={<RowVertical size={16} />}
                onClick={() => setViewType(ViewTypes.LIST)}
                type={viewType === 'list' ? 'primary' : 'default'}
              />
              <Button
                icon={<Grid1 size={16} />}
                onClick={() => setViewType(ViewTypes.GRID)}
                type={viewType === 'grid' ? 'primary' : 'default'}
              />
            </Space>
          </Flex>

          {viewType === 'list' ? (
            <Flex vertical gap={16}>
              {filteredApplications?.map((application) => (
                <ListView
                  key={application.id}
                  application={application}
                  currentApplication={organizationData?.current_application}
                />
              ))}
            </Flex>
          ) : (
            <Row gutter={[16, 16]}>
              {filteredApplications?.map((application) => (
                <Col span={12} key={application.id}>
                  <GridView
                    application={application}
                    currentApplication={organizationData?.current_application}
                  />
                </Col>
              ))}
            </Row>
          )}
          {!filteredApplications?.length && <Empty />}
        </Flex>
      )}
      trigger={'click'}
      arrow={false}
      placement="rightTop"
    >
      <Flex gap={8} align="center" className={styles.wrapper}>
        <Flex className={classNames(styles.navbarIcon, styles.applicationIcon)}>
          <AppIcon />
        </Flex>
        {!isCollapsed && (
          <Flex className={styles.navbarTitle} align="center">
            <span>
              {!isDefaultApp
                ? organizationData?.current_application?.name
                : t('application.title')}
            </span>
            <ArrowRight2 size={16} />
          </Flex>
        )}
      </Flex>
    </Popover>
  )
}
